/**
 * ProtectedRoute - Wrapper for routes that require authentication
 * 
 * Usage:
 * <Route path="/admin" element={
 *   <ProtectedRoute requiredRole="company_admin">
 *     <AdminPanel />
 *   </ProtectedRoute>
 * } />
 */

import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useEffect, useState } from 'react';

export function ProtectedRoute({ 
  children, 
  requiredRole = null,
  requireAuth = true 
}) {
  const { isAuthenticated, role, loading, user } = useAuth();
  const location = useLocation();
  const [waitTime, setWaitTime] = useState(0);

  // Dar más tiempo para que cargue la autenticación
  useEffect(() => {
    if (loading) {
      const timer = setInterval(() => {
        setWaitTime(prev => prev + 1);
      }, 1000);

      return () => clearInterval(timer);
    } else {
      setWaitTime(0);
    }
  }, [loading]);

  // Show loading state with more patience
  if (loading && waitTime < 10) { // Esperar hasta 10 segundos
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '100vh',
        background: '#0b1028',
        color: '#e9eefc'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div className="spinner" style={{ margin: '0 auto 16px' }}></div>
          <p style={{ margin: 0 }}>
            Verificando acceso... ({waitTime}s)
          </p>
          {waitTime > 5 && (
            <p style={{ margin: '8px 0 0', fontSize: '12px', opacity: 0.7 }}>
              Si esto tarda mucho, intenta recargar la página
            </p>
          )}
        </div>
      </div>
    );
  }

  // Si después de 10 segundos sigue cargando, mostrar opción de continuar
  if (loading && waitTime >= 10) {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '100vh',
        background: '#0b1028',
        color: '#e9eefc'
      }}>
        <div style={{ textAlign: 'center', maxWidth: '400px', padding: '20px' }}>
          <h2 style={{ color: '#f59e0b', marginBottom: '16px' }}>
            ⏳ Carga Lenta
          </h2>
          <p style={{ marginBottom: '20px' }}>
            La verificación de acceso está tardando más de lo normal.
          </p>
          <button 
            className="btn"
            onClick={() => window.location.reload()}
            style={{ marginRight: '12px' }}
          >
            Recargar Página
          </button>
          <button 
            className="copy"
            onClick={() => setWaitTime(0)}
          >
            Continuar de Todos Modos
          </button>
        </div>
      </div>
    );
  }

  // Check authentication
  if (requireAuth && !isAuthenticated) {
    // Redirect to login, saving the attempted location
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check role if required
  if (requiredRole) {
    const roleHierarchy = {
      'super_admin': 3,
      'company_admin': 2,
      'company_user': 1
    };

    const userLevel = roleHierarchy[role] || 0;
    const requiredLevel = roleHierarchy[requiredRole] || 0;

    if (userLevel < requiredLevel) {
      // User doesn't have required role
      return <Navigate to="/unauthorized" replace />;
    }
  }

  return children;
}

export default ProtectedRoute;
